import { computeCreditsUsage, CreditsUsageSeverity } from '@lib/utils/creditsUsage';
import { formatExpiresIn } from '@lib/utils/relativeTime';
import {
  formatCredits,
  formatCreditsWithUnit,
  formatDate,
  formatDurationToString,
} from '@lib/utils/units';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { approximateNumberOfSecondsOfMediaLeft, billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import { CreditsBreakdownButton } from './CreditsBreakdown';
import css from './Usage.module.scss';
import { UsageBar } from './UsageBar';
import { UsagePopout } from './UsagePopout';

const SEVERITY_COLOR: Record<CreditsUsageSeverity, string> = {
  normal: 'var(--mui-palette-primary-main)',
  warning: 'var(--mui-palette-warning-main)',
  critical: 'var(--mui-palette-error-main)',
};

/** Every note behind the bar: unexpired first, then expired, each newest first. */
const CreditNotesTable = observer(({ since }: { since: number }) => (
  <div className={css.creditNotes}>
    <span className={css.breakdownTitle}>Credit notes since {formatDate(since)}</span>
    <div className={css.creditNotesScroll}>
      <table className={css.creditNotesTable}>
        <thead>
          <tr>
            <th>Granted</th>
            <th>Left</th>
            <th>Expired unused</th>
            <th>Added</th>
            <th>Expiry date</th>
            <th>Expires</th>
          </tr>
        </thead>
        <tbody>
          {billingStore.usageWindowCreditNotes.map((creditNote) => (
            <tr key={creditNote._id} className={creditNote.expired ? css.expiredNote : undefined}>
              <td>{formatCredits(creditNote.credits.originalAmount)}</td>
              <td>{formatCredits(creditNote.credits.currentAmount)}</td>
              <td>{formatCredits(creditNote.credits.expiredAmount)}</td>
              <td>{formatDate(creditNote.createdAt)}</td>
              <td>{formatDate(creditNote.expiresAt)}</td>
              <td>{formatExpiresIn(creditNote.expiresAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
));

/**
 * Credits used out of those made spendable since the usage window opened (the oldest unexpired
 * note): every note issued since then, expired ones included, minus what expired unused. Short-
 * lived notes that ran out still count, so their spend shows here as it does in the breakdown.
 * Then the balance, and the breakdown behind a button.
 */
export const CreditsUsage = observer(() => {
  const creditsLeft = billingStore.creditsLeft;
  if (creditsLeft === null) return null;
  const since = billingStore.usageWindowStart;
  const spendable = billingStore.usageWindowSpendableCredits;
  const usage = spendable > 0 ? computeCreditsUsage(spendable, creditsLeft) : null;

  return (
    <div className={css.usageSection}>
      {usage && (
        <>
          <div className={css.header}>
            <span className={css.title}>
              Credits
              {since !== null && (
                <UsagePopout
                  label={<InfoOutlinedIcon />}
                  ariaLabel="Credit notes"
                  triggerClassName={css.infoButton}
                  onOpen={() => trackingStore.creditsBreakdownOpened({ panel: 'notes' })}
                >
                  <CreditNotesTable since={since} />
                </UsagePopout>
              )}
            </span>
            <span className={css.figures}>
              <span className={css[usage.severity]}>{formatCredits(usage.used)}</span> /{' '}
              {formatCredits(spendable)} · {usage.percent}%
            </span>
          </div>
          <UsageBar
            height={12}
            segments={[
              {
                label: 'Used',
                color: SEVERITY_COLOR[usage.severity],
                value: usage.used,
                tooltipText: `${formatCreditsWithUnit(usage.used)} used`,
              },
              {
                label: 'Left',
                color: 'transparent',
                value: usage.unfilled,
                // The balance, as the caption shows it: `unfilled` is clamped to the bar.
                tooltipText: `${formatCreditsWithUnit(creditsLeft)} left`,
              },
            ]}
          />
        </>
      )}
      <span className={css.caption}>
        <span className={css.captionValue}>{formatCreditsWithUnit(creditsLeft)}</span> left
        <span className={css.captionApproximation}>
          (≈{' '}
          {formatDurationToString(approximateNumberOfSecondsOfMediaLeft(creditsLeft), {
            detail: 2,
            precision: 0,
          })}{' '}
          of media indexing)
        </span>
        <CreditsBreakdownButton />
      </span>
    </div>
  );
});
